import { useEffect, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MessageCircle, Phone, Mail, Send, Users, Trash2, Edit, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { formatDateTime } from "@/lib/ui";

const CHANNELS = ["phone", "email", "sms", "secure_message", "in_person", "other"];
const CH_ICONS = { phone: Phone, email: Mail, sms: Send, secure_message: MessageCircle, in_person: Users, other: MessageCircle };

export default function CommunicationsTab({ clientId, isReadOnly, onChange }) {
  const { currentOrgId, user } = useAuth();
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      channel: "phone",
      direction: "outbound",
      occurred_at: new Date().toISOString().slice(0, 16),
      duration_minutes: "",
      subject: "",
      body: "",
      outcome: "",
    };
  }

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("communications")
      .select("*, profiles:staff_user_id(full_name)")
      .eq("client_id", clientId)
      .order("occurred_at", { ascending: false });
    setComms(data || []);
    setLoading(false);
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      channel: c.channel, direction: c.direction,
      occurred_at: c.occurred_at.slice(0, 16),
      duration_minutes: c.duration_minutes != null ? String(c.duration_minutes) : "",
      subject: c.subject || "", body: c.body || "", outcome: c.outcome || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      occurred_at: new Date(form.occurred_at).toISOString(),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
    };
    try {
      if (editing) {
        await apiFetch(`/communications/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Communication updated");
      } else {
        await apiFetch("/communications", { method: "POST",
          body: JSON.stringify({ ...payload, organization_id: currentOrgId, client_id: clientId, staff_user_id: user.id }) });
        toast.success("Communication logged");
      }
      setOpen(false); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this communication log entry?")) return;
    try {
      await apiFetch(`/communications/${c.id}`, { method: "DELETE" });
      toast.success("Deleted"); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="text-center py-12 text-sm text-[#57534E]">Loading communications…</div>;

  return (
    <div className="space-y-4" data-testid="communications-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">Communications</h3>
          <p className="text-xs text-[#57534E]">Log calls, emails, SMS and messages exchanged with this client.</p>
        </div>
        {!isReadOnly && (
          <Button data-testid="new-comm-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            <Plus size={14} className="mr-1.5" /> Log communication
          </Button>
        )}
      </div>

      {comms.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]">
          <MessageCircle size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium">No communications yet</p>
          <p className="text-xs text-[#57534E] mt-1">Every call, email, and message logged here becomes part of the client&apos;s timeline.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
          {comms.map((c) => {
            const Icon = CH_ICONS[c.channel] || MessageCircle;
            const DirIcon = c.direction === "inbound" ? ArrowDownLeft : ArrowUpRight;
            return (
              <div key={c.id} className="p-5" data-testid={`comm-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-[#F5EDE4] text-[#E8712C] flex items-center justify-center flex-shrink-0">
                      <Icon size={16} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="font-semibold capitalize">{c.channel.replace(/_/g, " ")}</span>
                        <span className="inline-flex items-center gap-1 text-xs bg-[#FAF8F5] px-2 py-0.5 rounded-full text-[#57534E]">
                          <DirIcon size={11} /> {c.direction}
                        </span>
                      </div>
                      <div className="text-xs text-[#57534E] mt-1">
                        {formatDateTime(c.occurred_at)}
                        {c.duration_minutes ? ` · ${c.duration_minutes} min` : ""}
                        {c.profiles?.full_name ? ` · by ${c.profiles.full_name}` : ""}
                      </div>
                      {c.subject && <div className="text-sm font-medium mt-2">{c.subject}</div>}
                      {c.body && <div className="text-sm text-[#1C1917] mt-1 whitespace-pre-wrap">{c.body}</div>}
                      {c.outcome && <div className="text-xs text-[#57534E] mt-2 italic">Outcome: {c.outcome}</div>}
                    </div>
                  </div>
                  {!isReadOnly && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" data-testid={`edit-comm-${c.id}`} onClick={() => openEdit(c)}><Edit size={13} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`del-comm-${c.id}`} onClick={() => remove(c)} className="text-red-600"><Trash2 size={13} /></Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit communication" : "Log communication"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({...form, channel: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="comm-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((ch) => <SelectItem key={ch} value={ch} className="capitalize">{ch.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({...form, direction: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="comm-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound (we contacted)</SelectItem>
                    <SelectItem value="inbound">Inbound (client contacted us)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Occurred at</Label>
                <Input type="datetime-local" value={form.occurred_at} onChange={(e) => setForm({...form, occurred_at: e.target.value})} className="mt-1.5" data-testid="comm-at" />
              </div>
              <div><Label>Duration (min)</Label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: e.target.value})} className="mt-1.5" />
              </div>
            </div>
            <div><Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="mt-1.5" data-testid="comm-subject" />
            </div>
            <div><Label>Details</Label>
              <Textarea rows={4} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} className="mt-1.5" data-testid="comm-body" placeholder="What was discussed?" />
            </div>
            <div><Label>Outcome / next step</Label>
              <Input value={form.outcome} onChange={(e) => setForm({...form, outcome: e.target.value})} className="mt-1.5" placeholder="e.g. scheduled follow-up call" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-comm-btn" onClick={save} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
